import { useEffect, useState } from "react";
import { getCompositionStates } from "../api";

export type CompositionState = "dressed" | "available";

/**
 * Sound design state of the audios shown in a list.
 *
 * Derived server-side from the compositions themselves rather than stored on the rows,
 * so it can never go stale: adding or removing a sound design changes the answer on the
 * next read, with nothing to keep in sync.
 *
 * Failing silently is deliberate — the badge is an indication, and a list must not break
 * because it could not be computed.
 */
export const useCompositionStates = (audioAIIds: string[]): Record<string, CompositionState> => {
    const [states, setStates] = useState<Record<string, CompositionState>>({});

    // Sorted and joined so the effect only re-runs when the set of ids really changes,
    // not on every re-render of the list.
    const key = [...audioAIIds].sort().join(",");

    useEffect(() => {
        const ids = key ? key.split(",") : [];
        if (ids.length === 0) {
            setStates({});
            return;
        }

        const token = localStorage.getItem("token");
        if (!token) return;

        let active = true;
        getCompositionStates({ token, audioAIIds: ids })
            .then((response) => {
                if (active) setStates(response?.states || {});
            })
            .catch(() => {
                if (active) setStates({});
            });

        return () => {
            active = false;
        };
    }, [key]);

    return states;
};

export default useCompositionStates;
